import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { Strings } from "@app/constants";
import { VehicleCard } from "@app/components";
import { style } from "../styles";
import { ChangeEvent, useEffect, useState } from "react";
import { useRequestState } from "@app/hooks";
import { selectMyOrders } from "../selectors";
import { clearOrder, getMyOrders } from "../slice";
import { MyOrdersPagination } from "./my-orders-paginations";
import { SellingCarDetails } from "./sell-orders";

const MyOrders = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState("all");

  const tabButtons = [
    { id: "all", label: "All Orders" },
    { id: "Buy", label: "Buy Orders" },
    { id: "Sell", label: "Sell Orders" },
    { id: "Test Drive", label: "Test Drive" },
  ];

  const { data: myOrdersData }: any = useRequestState({
    stateSelector: selectMyOrders,
  });

  useEffect(() => {
    dispatch(clearOrder());
    dispatch(
      getMyOrders({
        page: page,
        limit: 10,
        search: "",
        type: selectedTab === "all" ? "" : selectedTab,
      }),
    );
  }, [page, selectedTab, dispatch]);

  const handleChangePage = (event?: ChangeEvent<unknown>, value?: number) => {
    if (value !== undefined) setPage(value);
  };

  const filteredOrders =
    selectedTab === "all"
      ? myOrdersData?.data || myOrdersData
      : myOrdersData?.data?.filter(
          (order: any) =>
            order?.type?.toLowerCase() === selectedTab.toLowerCase(),
        );

  return (
    <Stack sx={style.container}>
      <Typography variant="h3" sx={style.heading}>
        {Strings.myOrders.myOrders}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, alignItems: "center", justifyContent: "center" }}
      >
        {tabButtons?.map((tab) => (
          <Button
            key={tab?.id}
            variant={selectedTab === tab?.id ? "contained" : "outlined"}
            onClick={() => {
              setSelectedTab(tab?.id);
              setPage(1);
            }}
            sx={{ borderRadius: 4, px: 3, py: 1 }}
          >
            {tab.label}
          </Button>
        ))}
      </Stack>

      {selectedTab === "Sell" ? (
        <SellingCarDetails
          sellData={filteredOrders}
          myOrdersData={myOrdersData}
          setSelectedTab={setSelectedTab}
        />
      ) : (
        <>
          <Stack sx={style.myOrdersCars} key={selectedTab}>
            {!filteredOrders?.length ? (
              <Stack
                sx={{
                  width: "100%",
                  height: 200,
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ fontSize: 24 }}>
                  {Strings.wishlist.noProductsFoundInMyOrders}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(PATH_DASHBOARD.productListPage)}
                >
                  {Strings.button.bookMyOrders}
                </Button>
              </Stack>
            ) : (
              filteredOrders.map((item: any) => {
                const { type } = item;
                const carData =
                  type === "Sell"
                    ? item?.sellCar
                    : type === "Buy"
                      ? item?.car
                      : type === "Test Drive"
                        ? item?.car || item
                        : {};
                if (!carData) return null;
                const normalizedVehicle = {
                  id: carData?.id,
                  image: carData?.images?.[0]?.url || "",
                  name: `${carData?.brand?.name || ""} ${
                    carData?.model?.name || ""
                  }`.trim(),
                  price: carData?.price ? `${carData?.price}` : "N/A",
                  transmission: carData?.transmission,
                  engineType: carData?.engineType,
                  driven: carData?.driven,
                  brand: carData?.brand?.name,
                  model: carData?.model?.name,
                };
                return (
                  <VehicleCard
                    key={`${normalizedVehicle?.id}-${
                      type?.toLowerCase() || "unknown"
                    }-${selectedTab}`}
                    vehicle={normalizedVehicle}
                    type={type}
                    onViewDetails={() =>
                      navigate(`${PATH_DASHBOARD.productDetail}/${carData?.id}`)
                    }
                  />
                );
              })
            )}
          </Stack>
          {myOrdersData && myOrdersData?.pagination?.total > 0 && (
            <Stack sx={{ alignItems: "center", mt: 3 }}>
              <MyOrdersPagination
                data={myOrdersData}
                page={page}
                handleChangePage={handleChangePage}
                theme={theme}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export { MyOrders };
